import React from 'react';
import {
  GraduationCap,
  School,
  Award,
  BookOpen,
  Brain,
  Sparkles,
  Star
} from 'lucide-react';

// Elegant, non-CV-style presentation of Teacher Kalah's qualifications.
// Two groups: Qualifications (formal certificates) and Experience.

const qualifications = [
  {
    icon: GraduationCap,
    title: "Master's in Early Childhood Education",
    institution: 'Open University Malaysia'
  },
  {
    icon: School,
    title: 'HEI Schools Teaching Diploma',
    institution: 'University of Helsinki'
  },
  {
    icon: Award,
    title: 'Teacher Trainer Certificate',
    institution: 'Montessori Centre International UK'
  },
  {
    icon: Award,
    title: 'International Teacher Certificate',
    institution: 'ECIS / University of Cambridge'
  },
  {
    icon: BookOpen,
    title: 'International Diploma in Early Childhood Education',
    institution: 'Montessori Centre International London'
  },
  {
    icon: Brain,
    title: 'Brain Gym Practitioner',
    institution: 'Certified Brain Gym training'
  }
];

const experience = [
  {
    icon: Star,
    title: 'Founder & Director',
    detail: 'Read Right Academy'
  },
  {
    icon: School,
    title: 'Former Kindergarten Headmistress',
    detail: 'School leadership & mentoring'
  },
  {
    icon: Sparkles,
    title: '20+ Years of Experience',
    detail: 'Early childhood education, phonics & teacher training'
  }
];

const Credentials = () => {
  return (
    <section
      id="credentials"
      data-testid="credentials-section"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block bg-[#3B4BA0]/10 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Credentials &amp; Experience
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            A Foundation of Training, Practice &amp; Care
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Teacher Kalah&apos;s qualifications and years of hands-on
            experience shape every lesson at Read Right Academy — bringing
            structure, warmth and depth to early literacy.
          </p>
        </div>

        {/* Qualifications */}
        <div className="mb-16">
          <h3 className="text-xs font-bold text-[#3B4BA0] uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="w-8 h-px bg-[#3B4BA0]" />
            Qualifications
          </h3>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
            {qualifications.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  data-testid={`qualification-${idx}`}
                  className="group flex items-start gap-4 bg-white rounded-2xl p-5 sm:p-6 border border-slate-100 hover:border-[#3B4BA0]/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-50 text-[#3B4BA0] flex items-center justify-center group-hover:bg-[#3B4BA0] group-hover:text-white transition-all duration-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 leading-snug mb-1">
                      {item.title}
                    </p>
                    <p className="text-sm text-gray-500">{item.institution}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Experience */}
        <div>
          <h3 className="text-xs font-bold text-teal-700 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="w-8 h-px bg-teal-600" />
            Experience
          </h3>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {experience.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  data-testid={`experience-${idx}`}
                  className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-md hover:border-teal-200 transition-all duration-300 text-center sm:text-left"
                >
                  <div className="mx-auto sm:mx-0 mb-4 w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-semibold text-gray-900 leading-snug mb-1">
                    {item.title}
                  </p>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Warm closing note */}
        <div className="mt-14 text-center max-w-2xl mx-auto">
          <p className="text-base sm:text-lg text-gray-600 italic leading-relaxed">
            Behind every credential is a simple commitment — to teach every
            child with patience, structure, and joy.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Credentials;
